import React, { useEffect, useState, useMemo } from "react";
import { tableProps } from "../../../../../../styles/muiProps";
import { MaterialReactTable } from "material-react-table";
import { columns } from "./columns";
import { getPvHours, getFabricationsByPlant, getFabricationTimesheet } from "./api";
import { useParams } from "react-router-dom";
import { usePMS } from "../PMSContext";

const Project5d = ({ plant = false, projectFilter = [] }) => {
  const { id, plantId } = useParams();
  const { plantProjects, selectedProjectIndices } = usePMS();
  const [data, setData] = useState([]);

  // Determine which plant projects to use for filtering
  const effectiveProjectIndices = useMemo(() => {
    if (!plant || plantProjects.length === 0) return [];

    // Use projectFilter if provided, otherwise use selectedProjectIndices from context
    const indices =
      Array.isArray(projectFilter) && projectFilter.length > 0
        ? projectFilter
        : selectedProjectIndices;

    // If no specific selection, use all projects (empty array means all)
    if (!Array.isArray(indices) || indices.length === 0) {
      return [];
    }

    return indices;
  }, [plant, plantProjects, projectFilter, selectedProjectIndices]);

  // Get project IDs for filtering
  const filterProjectIds = useMemo(() => {
    if (!plant || effectiveProjectIndices.length === 0) return [];
    return effectiveProjectIndices
      .map((idx) => {
        const proj = plantProjects[idx];
        return proj?.id || proj?.projectId || proj?.name || proj?.title;
      })
      .filter(Boolean);
  }, [plant, effectiveProjectIndices, plantProjects]);

  useEffect(() => {
    const getData = async () => {
      // Normal project mode: use existing PV hours API
      if (!plant) {
        if (!id) {
          setData([]);
          return;
        }
        try {
          const res = await getPvHours(id);
          console.log("hours ress", res);
          if (Array.isArray(res)) {
            res.splice(-5);
          }
          setData(res || []);
        } catch (e) {
          console.error("Error loading PV hours data:", e);
          setData([]);
        }
        return;
      }

      // Plant mode: get fabrications by plant and transform to PV hours format
      const activePlantId = plantId;
      if (!activePlantId) {
        console.warn("Plant mode: No plantId available");
        setData([]);
        return;
      }

      try {
        // Get all fabrications for the plant
        const fabrications = await getFabricationsByPlant(activePlantId);
        console.log("Plant fabrications:", fabrications);

        // Filter fabrications by project if filter is applied
        let filteredFabs = Array.isArray(fabrications) ? fabrications : [];
        if (filterProjectIds.length > 0) {
          filteredFabs = filteredFabs.filter((fab) => {
            const fabProjectId = fab?.projectId;
            return (
              fabProjectId &&
              filterProjectIds.some(
                (filterId) => String(fabProjectId) === String(filterId)
              )
            );
          });
        }

        // Get timesheet data for each fabrication and transform to PV hours format
        const transformedData = await Promise.all(
          filteredFabs.map(async (fab) => {
            let actualHours = 0;
            try {
              const timesheetData = await getFabricationTimesheet(fab.id);
              const timesheets = Array.isArray(timesheetData) ? timesheetData : [];
              if (timesheets.length > 0) {
                actualHours = timesheets.reduce(
                  (sum, ts) => sum + (Number(ts.hours) || 0),
                  0
                );
              }
            } catch (e) {
              console.warn(`Error fetching timesheet for fab ${fab.id}:`, e);
            }

            return {
              "Cost Code": fab.costCode || fab["Cost Code"] || "",
              "Material Description": fab.materialDescription || fab.document || fab["Material Description"] || fab.description || "",
              "Total Manhour": fab.estimatedHours || fab["Total Manhour"] || fab.hours || 0,
              actual_hours: actualHours,
              Remarks: fab.remarks || fab["Remarks"] || "",
            };
          })
        );

        console.log("Transformed PV hours data:", transformedData);
        setData(transformedData);
      } catch (e) {
        console.error("Error loading plant fabrication hours data:", e);
        setData([]);
      }
    };

    getData();
  }, [plant, id, plantId, filterProjectIds]);

  return (
    <MaterialReactTable
      // style={{ tableLayout: "auto" }}
      columns={columns}
      data={data || []}
      editDisplayMode="cell"
      // enableEditing={isEditTable}
      enableColumnActions={false}
      // enableRowActions
      positionActionsColumn="last"
      // enableColumnResizing={true}
      layoutMode={"grid"}
      {...tableProps}
      muiTableHeadCellProps={({ cell }) => ({
        sx: {
          borderRight: "1px solid rgba(224,224,224,1)",
          backgroundColor: "hsla(250, 86%, 97%, 1)",
          fontSize: "13px",
          pt: 2,
        },
      })}
      muiTableBodyCellProps={{
        sx: {
          padding: "8.5px 8px",
          // textAlign: "center",
          borderRight: "1px solid rgba(224,224,224,1)",
        },
      }}
      displayColumnDefOptions={{
        "mrt-row-actions": {
          size: 5,
          muiTableHeadCellProps: {
            align: "center",
          },
          muiTableBodyCellProps: {
            align: "center",
            sx: {
              p: 0,
              "& button": {
                ml: 0,
              },
            },
          },
        },
      }}
      // renderTopToolbarCustomActions={() => (
      //     <h4>Cost Comparison</h4>
      // )}
      defaultColumn={{
        minSize: 60, //allow columns to get smaller than default
        // maxSize: 160, //allow columns to get larger than default
        size: 80, //make columns wider by default
      }}
      initialState={{
        pagination: { pageSize: 30 },
        columnVisibility: { Component: false },
      }}
      // state={{
      //     isLoading,
      // }}
    />
  );
};

export default Project5d;
